import 'mapbox-gl/dist/mapbox-gl.css';
import './styles/map.css';
import { darkColors, lightColors, useColorMode, useColorModeValue as mode } from '@chaine/keychaine';
import React, { useEffect, useState } from 'react';
import { Map as ReactMapGl, MapProvider } from 'react-map-gl';
import { WebMercatorViewport } from 'viewport-mercator-project';
import { StyledBox } from '../styled-box';
import { availableLoads, currentLocation, routeLineCoordinates, stops } from './_data';
import { getStopsBounds } from './helpers';
import { CurrentLocationMarker } from './marker';
import { AvailableLoadMarkers } from './marker/available-load-marker';
import { StopsMarkers } from './marker/stop-markers';
import { RouteLine } from './route-line';
const defaultViewState = {
    bearing: 0,
    latitude: 39.828175,
    longitude: -98.5795,
    padding: {
        bottom: 0,
        left: 0,
        right: 0,
        top: 0
    },
    pitch: 0,
    zoom: 3
};
export const lightModeMapStyles = {
    satellite: 'mapbox://styles/mapbox/satellite-v9',
    street: 'mapbox://styles/shesupplypi/clf3i0oui000101ob17wub7w6'
};
export const darkModeMapStyles = {
    satellite: 'mapbox://styles/mapbox/satellite-v9',
    street: 'mapbox://styles/shesupplypi/clf3ilpsh000001jvxh4dlq6z'
};
const mapToken = String(process.env.VITE_MAPBOX_ACCESS_TOKEN);
export const MapWithRoute = (props) => {
    const { id, leftComponent, rightComponent, showAvailableLoads = false, initialViewPort = 'stops', showRoute = true, showStops = true, openAvailableLoadOnRender = false } = props;
    const { colorMode } = useColorMode();
    const mapStyle = mode('mapbox://styles/mapbox/streets-v11', 'mapbox://styles/mapbox/dark-v10');
    const [style, setStyle] = useState(mapStyle);
    useEffect(() => {
        setStyle(mapStyle);
    }, [colorMode, mapStyle]);
    const handleSetLocationOnFirstRender = (map) => {
        if (initialViewPort === 'stops') {
            const viewport = new WebMercatorViewport({ height: 600, width: 800 });
            const { longitude, latitude, zoom } = viewport.fitBounds(getStopsBounds(stops), {
                offset: [-100, -100],
                padding: 10
            });
            const stopZoom = zoom < 11 ? zoom : 11;
            map?.easeTo({ center: { lat: latitude, lng: longitude }, duration: 1000, zoom: stopZoom });
        }
        else if (initialViewPort === 'currentLocation') {
            map?.easeTo({
                center: {
                    lat: currentLocation.location.coordinates.latitude,
                    lng: currentLocation.location.coordinates.longitude
                },
                duration: 1000,
                zoom: 5
            });
        }
        else {
            map?.easeTo({
                center: { lat: defaultViewState.latitude, lng: defaultViewState.longitude },
                duration: 1000,
                zoom: defaultViewState.zoom
            });
        }
    };
    return (React.createElement(StyledBox, { px: 3, borderColorProps: { light: 'blue.500', dark: 'blue.800' }, height: "full", width: 'full', sx: {
            '.mapboxgl-control-container': {
                display: 'none !important'
            }
        } },
        leftComponent,
        React.createElement(MapProvider, null,
            React.createElement(ReactMapGl, { id: id, reuseMaps: true, onLoad: ({ target: map }) => {
                    handleSetLocationOnFirstRender(map);
                }, initialViewState: defaultViewState, mapStyle: style, mapboxAccessToken: mapToken },
                showStops && React.createElement(StopsMarkers, { stops: stops }),
                showAvailableLoads && (React.createElement(AvailableLoadMarkers, { id: id, loads: availableLoads, openAvailableLoadOnRender: openAvailableLoadOnRender })),
                showRoute && (React.createElement(RouteLine, { coordinates: routeLineCoordinates, id: "route", routeColor: mode(lightColors.blue[400], darkColors.blue[400]) })),
                showRoute && (React.createElement(CurrentLocationMarker, { color: mode(lightColors.blue[400], darkColors.blue[400]), ...currentLocation })))),
        rightComponent));
};
